import React, {useCallback, useEffect, useState} from 'react';
import {Box, IconButton, LinearProgress, SvgIcon, TableCell, TableRow, Typography, useMediaQuery} from "@mui/material";
import localStrings from "../../../localStrings";
import useAuth from "../../../hooks/useAuth";
import {executeQueryUtil} from "../../../utils/gqlUtil";
import ClipLoaderComponent from "../../../components/ClipLoaderComponent";
import GeneralResult from "../../../components/CrudFrom/GeneralResult";
import {getProductsQuery} from "../../../gql/ProductGql";
import {ProductListSearch} from "./ProductListSearch";
import {getCategoriesQuery} from "../../../gql/CategoryGql";
import * as GraphUtils from "../../../Graphs/BarGraphUtils";
import {buildOptionOptionListMap, buildProductCategoryMap} from "../../../Graphs/BarGraphUtils";
import ChevronDownIcon from "@untitled-ui/icons-react/build/esm/ChevronDown";
import ChevronRightIcon from "@untitled-ui/icons-react/build/esm/ChevronRight";
import ProductDetail from "./ProductDetail";
import Distribution from "./Distribution";
import {getOptionListsQuery} from "../../../gql/OptionListsGql";
import {filterSingleDay} from "../../Home/FilterInfoOverview";
import {getTableCellDynStyle} from "../StaffData/Results";
import {formatTotal} from "../../../utils/currencyUtil";


const Results = ({className, ...rest}) => {
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
    const { currentBrand, currentEstablishment, orders, sumUpDays,
        filter, mergedSumUpDay, loading } = useAuth();
    const [itemSource, setItemSource] = useState(null);
    const [itemSourceFiltered, setItemSourceFiltered] = useState(null);
    const [ordersFiltered, setOrdersFiltered] = useState(orders);
    const [categoryOptions, setCategoryOptions] = useState(null);
    const [mapProductCategory, setMapProductCategory] = useState([]);
    const [mapOptionOptionList, setMapOptionOptionList] = useState([]);
    const [maxSale, setMaxSale] = useState(0);
    const [maxCount, setCount] = useState(0);
    const [currentProduct, setCurrentProduct] = useState(null);

    const handleProductToggle = useCallback((productId) => {
        setCurrentProduct((prevProductId) => {
            if (prevProductId === productId) {
                return null;
            }

            return productId;
        });
    }, []);

    function completeProducts(products) {
        if (!orders && !sumUpDays) {
            return products;
        }


        if (filter?.filterType === filterSingleDay) {

            products.forEach(product => {
                let totalInfo = GraphUtils.getTotalInfoByProduct(product, orders);
                product.count = totalInfo.count;
                product.total = totalInfo.total;
            })
        }
        else {
            products.forEach(product => {
                if (mergedSumUpDay && mergedSumUpDay.detailbyStaff) {
                    let totalInfo = GraphUtils.getTotalInfoBySumUpDayByProduct(product, mergedSumUpDay);
                    product.count = totalInfo.count;
                    product.total = totalInfo.total;
                } else {
                    product.count = 0;
                    product.total = 0;
                }
            })
        }


        const maxSale = Math.max(...products.map(x => x.total));
        const maxCount = Math.max(...products.map(x => x.count));

        setMaxSale(maxSale);
        setCount(maxCount);

        products.forEach(product => {
            // let totalInfo = GraphUtils.getTotalInfo(product, orders);
            product.countRatio = Math.floor(100 * (product.count / maxCount));
            product.totalRatio = Math.floor(100 * (product.total / maxSale));
        })

        products = products.filter(x => x.count > 0);
        return products;
    }

    useEffect(() => {
        if (!currentBrand() || !currentEstablishment()) {
            return;
        }

        async function setItemSourceAsync() {
            // setLoading(true);
            let res = await executeQueryUtil(getProductsQuery(currentBrand().id, currentEstablishment().id));
            let products = res?.data?.getProducts.map(x => JSON.parse(x.content));
            products = completeProducts(products);

            setItemSource(products)
            setItemSourceFiltered([...products])

            const resCat = await executeQueryUtil(getCategoriesQuery(currentBrand().id, currentEstablishment().id));
            const categories = resCat?.data?.getCategories.map(x => JSON.parse(x.content));

            const mapProductCategory = buildProductCategoryMap(products, categories);
            setMapProductCategory(mapProductCategory)
            setCategoryOptions(categories.map (x => {
                return {
                    label: x.name,
                    value: x.id
                }
            }));

            const resOptionLists = await executeQueryUtil(getOptionListsQuery(currentBrand().id, currentEstablishment().id));
            const optionsLists = resOptionLists?.data?.getOptionLists.map(x => JSON.parse(x.content));

            var mapOptionOptionList = buildOptionOptionListMap(optionsLists);
            setMapOptionOptionList(mapOptionOptionList);
            // setLoading(false);
        }
        setItemSourceAsync();
    }, [currentBrand(), currentEstablishment(), orders, filter, mergedSumUpDay]);

    const getTableCell = (item) => {
        return(
            <>
                <TableRow>
                    <TableCell
                        padding="checkbox"
                        sx={{
                            ...(item.id === currentProduct ? {
                                        position: 'relative',
                                        '&:after': {
                                            position: 'absolute',
                                            content: '" "',
                                            top: 0,
                                            left: 0,
                                            backgroundColor: 'primary.main',
                                            width: 3,
                                            height: 'calc(100% + 1px)'
                                        },
                                        witdth: '25px',
                                        padding: lgUp ? 0 : 'inherit',
                                        // padding: lgUp ? 0 : 'inherit'

                                    }
                                    :
                                    {
                                        witdth: '25px',
                                        padding: lgUp ? 0 : 'inherit',
                                    }
                            )
                        }}
                    >
                        <IconButton
                            onClick={() => handleProductToggle(item.id)}
                            sx={{
                                padding: lgUp ? '8px' : '1px', // Réduction maximale du padding
                                minWidth: 0,                  // Évite les tailles minimales trop larges
                                width: lgUp ? '24px' : 'auto',
                                height: lgUp ? '24px' : 'auto',
                            }}
                        >
                            <SvgIcon fontSize="small">
                                {item.id === currentProduct ? <ChevronDownIcon /> : <ChevronRightIcon />}
                            </SvgIcon>
                        </IconButton>
                    </TableCell>
                    <TableCell sx={getTableCellDynStyle(lgUp)}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex'
                            }}
                        >
                            {item.image
                                &&
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        backgroundColor: 'neutral.50',
                                        backgroundImage: `url(${item.image})`,
                                        backgroundPosition: 'center',
                                        backgroundSize: 'cover',
                                        borderRadius: 1,
                                        display: 'flex',
                                        height: 80,
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        width: 80
                                    }}
                                />
                            }
                            <Box
                                sx={{
                                    cursor: 'pointer',
                                    ml: 2
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {item.name}
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    variant="body2"
                                >
                                    {
                                        (categoryOptions || []).find(c => c.value === item.category_id)?.label || ""
                                    }
                                </Typography>
                            </Box>
                        </Box>
                    </TableCell>

                    <TableCell sx={getTableCellDynStyle(lgUp)}>
                        <LinearProgress
                            value={item.totalRatio}
                            variant="determinate"
                            color={getColor(item.totalRatio)}
                            sx={{
                                height: 8,
                                width: '100%'
                            }}
                        />
                        <Typography
                            color="text.secondary"
                            variant="body2"
                        >
                            {formatTotal(item.total) + " €"}

                        </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellDynStyle(lgUp)}>
                        <LinearProgress
                            value={item.countRatio}
                            variant="determinate"
                            color={getColor(item.countRatio)}
                            sx={{
                                height: 8,
                                width: '100%'
                            }}
                        />
                        <Typography
                            color="text.secondary"
                            variant="body2"
                        >
                            {
                                item.count
                            }
                        </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellDynStyle(lgUp)}>
                        {/*{JSON.stringify(product.skus || {})}*/}
                        {item.skus && item.skus.length > 0 ? item.skus[0].price : 0}
                    </TableCell>
                </TableRow>

                {item.id === currentProduct &&
                    <TableRow>
                        <TableCell
                            colSpan={7}
                            sx={{
                                p: 0,
                                position: 'relative',
                                '&:after': {
                                    position: 'absolute',
                                    content: '" "',
                                    top: 0,
                                    left: 0,
                                    backgroundColor: 'primary.main',
                                    width: 3,
                                    height: 'calc(100% + 1px)'
                                }
                            }}
                        >
                            <ProductDetail
                                noPrice={item.total === 0}
                                mapOptionOptionList={mapOptionOptionList}
                                product = {itemSource.find(p => p.id === currentProduct)}/>
                        </TableCell>
                    </TableRow>
                }
            </>
        )
    }

    const getTableCellColumn = () => {
        return(
            <>
                <TableCell
                    sx={{
                        // Sur mobile : plus de marginLeft = 0
                        // Sur desktop : marginLeft = 2
                        witdth: '25px',
                        padding: lgUp ? 0 : 'inherit',
                    }}
                />
                <TableCell sx={getTableCellDynStyle(lgUp)}>
                    {localStrings.name}
                </TableCell>
                <TableCell sx={getTableCellDynStyle(lgUp)}>
                    {localStrings.totalSales}
                </TableCell>
                <TableCell sx={getTableCellDynStyle(lgUp)}>
                    {localStrings.salesCount}
                </TableCell>
                <TableCell sx={getTableCellDynStyle(lgUp)}>
                    {localStrings.price}
                </TableCell>
            </>
        )
    }

    function getColor(ratio) {
        if (ratio >= 66) {
            return 'success';
        }
        if (ratio >= 33) {
            return 'warning';
        }
        return 'error';
    }





    const sortOptions = [
        {
            value: 'name|asc',
            label: localStrings.name
        },
        {
            value: 'total|desc',
            label: localStrings.sale
        },
        {
            value: 'count|desc',
            label: localStrings.count
        },
    ];

    function applyFilter(filter) {
        if (!filter || !filter.category || filter.category.length === 0) {
            setItemSourceFiltered(itemSource)
            setOrdersFiltered(orders)
            return
        }



        let filteredProduct = itemSource.filter(p => filter.category.includes(p.category_id));
        setItemSourceFiltered(filteredProduct);

        let ordersFiltered = [];
        // mapProductCategory.forEach((value, key) => {
        (orders || []).forEach(order => {
            let filteredItems = order.items
                .filter(p => {
                    return filter.category.includes(  mapProductCategory[p.sku_ref]  )
                });
            if (filteredItems.length > 0) {
                ordersFiltered.push({
                    ...order,
                    items: filteredItems
                })
            }

        })

        setOrdersFiltered(ordersFiltered);
    }

    return (
        <>
            { (itemSource && !loading) ?
                <GeneralResult
                    className={className}
                    getPathToData={(result) => result}
                    getTableCell={getTableCell}
                    getTableCellColumn={getTableCellColumn}
                    doNotUseTableRow
                    sortOptions={sortOptions}
                    searchProperties={['name']}
                    itemsSource={itemSourceFiltered}
                    tabs={[
                        {
                            value: 'distribution',
                            label: localStrings.distribution,
                            component: <Distribution
                                orders={ordersFiltered}
                            />
                        },
                    ]}
                    headerItem={<ProductListSearch
                        onFiltersChange={(filter) => {applyFilter(filter)}}
                        categoryOptions={categoryOptions || []}
                    />}
                >

                </GeneralResult>
                :
                <ClipLoaderComponent/>
            }
        </>
    )

};


export default Results;
